// src/services/tasks.ts
import { supabase } from "../lib/supabase";
import type { ID, Task, TaskStatus, Priority } from "../types/domain";

type NewTask = {
  project_id: ID;
  title: string;
  status?: TaskStatus;
  description?: string | null;
  priority?: Priority | null;
  deadline?: string | null; // 'YYYY-MM-DD'
  position?: number | null; // si no se pasa, se calcula
  assignee_id?: ID | null; // usuario asignado
  created_by?: ID | null; // usuario que crea la tarea
};

export async function getNextPosition(projectId: ID, status: TaskStatus) {
  const { data, error } = await supabase
    .from("tasks")
    .select("position")
    .eq("project_id", projectId)
    .eq("status", status)
    .not("position", "is", null)
    .order("position", { ascending: false })
    .limit(1);

  if (error) return 1;
  const last = (data && data[0]?.position) ?? 0;
  return (Number.isFinite(last) ? Number(last) : 0) + 1;
}

export async function createTask(payload: NewTask) {
  const status: TaskStatus = payload.status ?? "todo";
  const position =
    typeof payload.position === "number"
      ? payload.position
      : await getNextPosition(payload.project_id, status);

  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  const userId = user?.id || null;

  const { data, error } = await supabase
    .from("tasks")
    .insert({
      project_id: payload.project_id,
      title: payload.title,
      status,
      description: payload.description ?? null,
      priority: payload.priority ?? null,
      deadline: payload.deadline ?? null,
      position,
      assignee_id: payload.assignee_id ?? userId, // Por defecto se asigna al creador
      created_by: payload.created_by ?? userId,
    })
    .select()
    .single();

  return { data: data as Task | null, error };
}

export async function updateTask(id: ID, patch: Partial<Task>) {
  const { data, error } = await supabase
    .from("tasks")
    .update({
      title: patch.title,
      description: patch.description ?? null,
      status: patch.status,
      priority: (patch.priority as Priority | null) ?? null,
      deadline: patch.deadline ?? null,
      position: typeof patch.position === "number" ? patch.position : undefined,
    })
    .eq("id", id)
    .select()
    .single();

  return { data: data as Task | null, error };
}

export async function deleteTask(id: ID) {
  const { error } = await supabase
    .from("tasks")
    .update({ status: "deleted" })
    .eq("id", id);
  return { error };
}

/**
 * Reordena en bloque. Intenta UPSERT (requiere INSERT+UPDATE en RLS).
 * Si falla (p.ej. por políticas), hace fallback a updates por fila (sólo UPDATE).
 */
export async function reorderTasks(
  updates: Array<{ id: ID; position: number; status?: TaskStatus }>
) {
  if (!updates.length) return { error: null };

  // 1) Intento: upsert
  const upsert = await supabase
    .from("tasks")
    .upsert(updates, { onConflict: "id" });

  if (!upsert.error) return { error: null };

  // 2) Fallback: updates por fila (sólo UPDATE)
  try {
    await Promise.all(
      updates.map((u) =>
        supabase
          .from("tasks")
          .update({
            position: u.position,
            ...(u.status ? { status: u.status } : {}),
          })
          .eq("id", u.id)
      )
    );
    return { error: null };
  } catch (e: any) {
    return { error: e };
  }
}
